/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Button, Checkbox, TableProps, Tooltip } from 'antd';
import { MouseEvent as ReactMouseEvent, useMemo } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { EStyleObj } from '~src/types';
import { DbShare, offerContract } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { formatDate } from '~src/utils/formatDate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover } from 'antd';
import {
  getContractVisual,
  getOfferStatusVisual,
  getShareStatusVisual,
  getShareStatusVisualIntl,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';

type UseColumnsProps = {
  viewOnly?: boolean;
  onMenuDetailsClick: (
    e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
    record: DbShare
  ) => void;
  styles: EStyleObj;
  // Secondary share keys have no contract-offer data (per spec) - used by
  // the expandable secondary-vendors drawer to reuse this same hook.
  hideContractsColumn?: boolean;
};

export const useColumns = ({
  viewOnly,
  onMenuDetailsClick,
  styles,
  hideContractsColumn,
}: UseColumnsProps) => {
  const intl = useIntl();

  return useMemo<TableProps<DbShare>['columns']>(() => {
    const resultingArray: TableProps<DbShare>['columns'] = [];

    if (!viewOnly) {
      resultingArray.push({
        title: '',
        width: 40,
        align: 'center',
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              // style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => onMenuDetailsClick(e, record)}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      });
    }

    resultingArray.push(
      {
        title: intl.formatMessage({ id: 'status' }),
        dataIndex: 'share_key_state',
        width: viewOnly ? 50 : 60,
        align: 'center',
        render: (_: any, record: DbShare) => {
          const { icon, color, text } = getShareStatusVisualIntl(intl, record);
          return (
            <Tooltip title={text}>
              <div css={styles.alignCenterWrapper} style={{ color }}>
                {icon}
              </div>
            </Tooltip>
          );
        },
        // sorter: viewOnly ? false : (a, b) => a.state - b.state,
      },
      {
        title: intl.formatMessage({ id: 'company-name' }),
        dataIndex: 'share_key_title',
        // sorter: viewOnly ? false : (a, b) => a.title.localeCompare(b.title),
      },
      {
        title: intl.formatMessage({ id: 'email' }),
        dataIndex: 'share_key_email',
        // sorter: viewOnly ? false : (a, b) => a.email.localeCompare(b.email),
        ellipsis: !viewOnly,
      },
      {
        title: intl.formatMessage({ id: 'contact-name' }),
        dataIndex: 'claimer',
        ellipsis: !viewOnly,
        // Fixed: claimer is an object { full_name, company_name ... }, not a string
        render: (claimer: any) => (claimer?.full_name ? claimer.full_name : ''),
      },
      // {
      //   title: intl.formatMessage({ id: 'note' }),
      //   dataIndex: 'share_description',
      //   ellipsis: !viewOnly,
      // },
      {
        title: intl.formatMessage({ id: 'shares-tab-table-opened-column' }),
        dataIndex: 'share_key_opened_at',
        width: 110,
        ellipsis: true,
        render: (opened_at) => {
          if (!opened_at) return '—';
          const shortDate = formatDate(opened_at, 'short');
          const fullDate = formatDate(opened_at, 'long');
          return (
            <Tooltip title={fullDate} placement="topLeft">
              <span>{shortDate}</span>
            </Tooltip>
          );
        },
      }
    );

    if (!viewOnly) {
      resultingArray.push({
        title: intl.formatMessage({ id: 'accepted' }),
        key: 'accepted',
        width: 110,
        ellipsis: true,
        render: (_: any, record: DbShare) => {
          const isRejected = record.share_key_state === 5;
          const date = isRejected
            ? record.share_key_last_rejected_at
            : record.share_key_claimed_at;

          if (!date) return '—';

          const shortDate = formatDate(date, 'short');
          const fullDate = formatDate(date, 'long');

          return (
            <Tooltip title={fullDate} placement="topLeft">
              <span style={isRejected ? { color: '#ff4d4f' } : undefined}>
                {shortDate}
              </span>
            </Tooltip>
          );
        },
      });
    }

    if (!hideContractsColumn) {
      resultingArray.push({
        title: intl.formatMessage({ id: 'contracts' }),
        dataIndex: 'contracts',
        width: 110,
        ellipsis: true,
        render: (_: any, record: DbShare) => {
          const offers = record.offers;
          if (offers.length === 0) {
            return null;
          } else if (offers.length === 1) {
            const { color, text } = getOfferStatusVisual(intl, offers[0]);

            let icon = null;
            if (color === 'grey')
              icon = <FontAwesomeIcon icon={['far', 'stopwatch']} />;
            else if (color === 'green')
              icon = <FontAwesomeIcon icon={['fas', 'memo-circle-check']} />;
            else if (color === 'red')
              icon = <FontAwesomeIcon icon={['far', 'ban']} />;
            else if (color === 'orange')
              icon = <FontAwesomeIcon icon={['far', 'pen']} />;

            return (
              <Popover
                trigger="click"
                content={
                  <div
                    css={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '15px',
                      padding: '10px',
                    }}>
                    {offers.map((offer, index) => {
                      const { color, text } = getOfferStatusVisual(intl, offer);
                      return (
                        <div
                          key={index}
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}>
                          <div
                            css={[
                              { height: '100%', width: '5px' },
                              styles[color],
                            ]}
                          />
                          <div
                            style={{
                              fontSize: '12px',
                              lineHeight: '1.4',
                              display: 'flex',
                              flexDirection: 'column',
                            }}>
                            <div>
                              <b>Contract ID:</b> #{offer.contract_id}
                            </div>
                            <div>
                              <b>Contract Ref:</b> #{offer.contract_reference}
                            </div>
                            {/*{offer.offer_id && (*/}
                            {/*  <div>*/}
                            {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                            {/*  </div>*/}
                            {/*)}*/}
                            {/*<div>*/}
                            {/*  <b>Offer created:</b>{' '}*/}
                            {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                            {/*</div>*/}
                            <div>
                              <b>State: {text}</b>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }>
                <div
                  style={{
                    color,
                    border: `1px solid ${color}`,
                    borderRadius: '3px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '3px',
                  }}>
                  {icon}
                  <strong>{text}</strong>
                </div>
              </Popover>
            );
          } else {
            // If more than 3 offers, show a summary with a popover.
            // If there are more than 3 offers, show first 3 + a "square" with hiddenCount
            if (offers.length > 4) {
              const visibleOffers = offers.slice(0, 3);
              const hiddenCount = offers.length - visibleOffers.length;
              return (
                <Popover
                  trigger="click"
                  content={
                    <div
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '15px',
                        padding: '10px',
                      }}>
                      {offers.map((offer, index) => {
                        const { color, text } = getOfferStatusVisual(
                          intl,
                          offer
                        );
                        return (
                          <div
                            key={index}
                            css={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                            }}>
                            <div
                              css={[
                                { height: '100%', width: '5px' },
                                styles[color],
                              ]}
                            />
                            <div
                              style={{
                                fontSize: '12px',
                                lineHeight: '1.4',
                                display: 'flex',
                                flexDirection: 'column',
                              }}>
                              <div>
                                <b>Contract ID:</b> #{offer.contract_id}
                              </div>
                              <div>
                                <b>Contract Ref:</b> #{offer.contract_reference}
                              </div>
                              {/*{offer.offer_id && (*/}
                              {/*  <div>*/}
                              {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                              {/*  </div>*/}
                              {/*)}*/}
                              {/*<div>*/}
                              {/*  <b>Offer created:</b>{' '}*/}
                              {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                              {/*</div>*/}
                              <div>
                                <b>State: {text}</b>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }>
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '4px', // control spacing between circles
                    }}>
                    {visibleOffers.map((offer, index) => {
                      const { color, text } = getOfferStatusVisual(intl, offer);
                      return (
                        <Tooltip
                          key={index}
                          placement="top"
                          title={
                            <div
                              style={{
                                fontSize: '12px',
                                lineHeight: '1.4',
                                display: 'flex',
                                flexDirection: 'column',
                              }}>
                              <div>
                                <b>Contract ID:</b> #{offer.contract_id}
                              </div>
                              <div>
                                <b>Contract Ref:</b> #{offer.contract_reference}
                              </div>
                              {/*{offer.offer_id && (*/}
                              {/*  <div>*/}
                              {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                              {/*  </div>*/}
                              {/*)}*/}
                              {/*<div>*/}
                              {/*  <b>Offer created:</b>{' '}*/}
                              {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                              {/*</div>*/}
                              <div>
                                <b>State: {text}</b>
                              </div>
                            </div>
                          }>
                          <div
                            css={[styles.tagPoint, styles[color]]}
                            style={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      );
                    })}

                    {/* Square button */}
                    <div
                      css={{
                        width: '23px',
                        height: '23px',
                        backgroundColor: 'white',
                        border: '1px solid gray',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'black',
                        fontWeight: 'lighter',
                        fontSize: '13px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                      title="More contracts">
                      <strong>+{hiddenCount}</strong>
                    </div>
                  </div>
                </Popover>
              );
            } else {
              // If there are exactly 2 or 3 offers, show them all.
              return (
                <Popover
                  trigger="click"
                  content={
                    <div
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '15px',
                        padding: '10px',
                      }}>
                      {offers.map((offer, index) => {
                        const { color, text } = getOfferStatusVisual(
                          intl,
                          offer
                        );
                        return (
                          <div
                            key={index}
                            css={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                            }}>
                            <div
                              css={[
                                { height: '100%', width: '5px' },
                                styles[color],
                              ]}
                            />
                            <div
                              style={{
                                fontSize: '12px',
                                lineHeight: '1.4',
                                display: 'flex',
                                flexDirection: 'column',
                              }}>
                              <div>
                                <b>Contract ID:</b> #{offer.contract_id}
                              </div>
                              <div>
                                <b>Contract Ref:</b> #{offer.contract_reference}
                              </div>
                              {/*{offer.offer_id && (*/}
                              {/*  <div>*/}
                              {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                              {/*  </div>*/}
                              {/*)}*/}
                              {/*<div>*/}
                              {/*  <b>Offer created:</b>{' '}*/}
                              {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                              {/*</div>*/}
                              <div>
                                <b>State: {text}</b>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }>
                  <div css={styles.alignCenterWrapper} style={{ gap: '3px' }}>
                    {offers.map((offer, index) => {
                      const { color, text } = getOfferStatusVisual(intl, offer);
                      return (
                        <Tooltip
                          key={index}
                          placement="top"
                          title={
                            <div
                              style={{
                                fontSize: '12px',
                                lineHeight: '1.4',
                                display: 'flex',
                                flexDirection: 'column',
                              }}>
                              <div>
                                <b>Contract ID:</b> #{offer.contract_id}
                              </div>
                              <div>
                                <b>Contract Ref:</b> #{offer.contract_reference}
                              </div>
                              {/*{offer.offer_id && (*/}
                              {/*  <div>*/}
                              {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                              {/*  </div>*/}
                              {/*)}*/}
                              {/*<div>*/}
                              {/*  <b>Offer created:</b>{' '}*/}
                              {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                              {/*</div>*/}
                              <div>
                                <b>State: {text}</b>
                              </div>
                            </div>
                          }>
                          <div
                            css={[styles.tagPoint, styles[color]]}
                            style={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      );
                    })}
                  </div>
                </Popover>
              );
            }
          }
        },
      });
    }

    return resultingArray;
  }, [intl, styles.icons, styles.small, hideContractsColumn]);
};
